import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { IOrderMenuItem } from '../pages/v2-order-pay/models/order-menu.models';
import { filter, map } from 'rxjs/operators';
import {
  ItemModifierGroupUI,
  ItemModifierUI
} from '../pages/v2-order-pay/models/item-modifier-ui.model';
import { LocationStore } from 'src/app/stores/location.store';
import { OrderMenuStore } from 'stores/order-menu.store';
import { RxUtil } from 'utility/rx.util';
import { I18nStore } from 'stores/i18n.store';

export interface ISelectedItemDetails {
  quantity: number;
  comment: string;
}

export interface ISelectedModifier {
  id: string;
  quantity: number;
  maximumQty: number;
}

@Injectable({
  providedIn: 'root'
})
export class ItemDetailsStore {
  constructor(
    private locationStore: LocationStore,
    private orderMenuStore: OrderMenuStore,
    private i18nStore: I18nStore
  ) {}

  private readonly _selectedItemId$ = new BehaviorSubject<string | null>(null);

  readonly selectedItem$: Observable<IOrderMenuItem> = combineLatest([
    this._selectedItemId$,
    this.orderMenuStore.orderMenuItems$
  ]).pipe(
    map(([selectedItemId, orderMenuItems]) =>
      orderMenuItems.find((item) => item.id === selectedItemId)
    ),
    filter(RxUtil.inputIsNotNullOrUndefined)
  );

  get selectedItemId(): string | null {
    return this._selectedItemId$.getValue();
  }

  set selectedItemId(v: string | null) {
    this._selectedItemId$.next(v);
  }

  private readonly _selectedItemDetails$: BehaviorSubject<ISelectedItemDetails> =
    new BehaviorSubject<ISelectedItemDetails>({
      quantity: 1,
      comment: ''
    });
  readonly selectedItemDetails$: Observable<ISelectedItemDetails> =
    this._selectedItemDetails$.asObservable();

  get selectedItemDetails(): ISelectedItemDetails {
    return this._selectedItemDetails$.getValue();
  }

  setSelectedItemQuantity(quantity: number) {
    this._selectedItemDetails$.next({
      ...this._selectedItemDetails$.getValue(),
      quantity
    });
  }

  setSelectedItemDetailsComment(comment: string) {
    this._selectedItemDetails$.next({
      ...this._selectedItemDetails$.getValue(),
      comment
    });
  }

  private readonly modifierSelectionMap$ = new BehaviorSubject<
    Map<string, Set<ISelectedModifier>>
  >(new Map<string, Set<ISelectedModifier>>());

  selectModifier(group: ItemModifierGroupUI, modifier: ISelectedModifier) {
    const selectionMap = this.modifierSelectionMap$.getValue();
    const groupSet =
      selectionMap.get(group.ownershipChainKey) ||
      (selectionMap
        .set(group.ownershipChainKey, new Set<ISelectedModifier>())
        .get(group.ownershipChainKey) as Set<ISelectedModifier>);

    if (this.isMultiModifierQuantitySelectionEnabled(group)) {
      this.selectMultiQuantityModifier(groupSet, group, modifier);
    } else {
      this.selectSingleQuantityModifier(groupSet, group, modifier);
    }

    this.modifierSelectionMap$.next(selectionMap);
  }

  private selectSingleQuantityModifier(
    groupSet: Set<ISelectedModifier>,
    group: ItemModifierGroupUI,
    modifier: ISelectedModifier
  ) {
    const wasModifierSelected = !!this.findSelectedModifier(
      groupSet,
      modifier.id
    );

    if (group.maximum === 1) {
      // behave like radio group
      groupSet.clear();
    }
    const currentSelectedModifier = this.findSelectedModifier(
      groupSet,
      modifier.id
    );
    if (group.maximum === 0 || groupSet.size < group.maximum) {
      if (currentSelectedModifier) {
        groupSet.delete(currentSelectedModifier);
      } else if (!wasModifierSelected || group.minimum > 0) {
        groupSet.add(modifier);
      }
    } else if (groupSet.size === group.maximum) {
      if (currentSelectedModifier) {
        groupSet.delete(currentSelectedModifier);
      }
    }
  }

  private selectMultiQuantityModifier(
    groupSet: Set<ISelectedModifier>,
    group: ItemModifierGroupUI,
    modifier: ISelectedModifier
  ) {
    const currentSelectedModifier = this.findSelectedModifier(
      groupSet,
      modifier.id
    );
    if (currentSelectedModifier) {
      if (modifier.quantity === 0) {
        groupSet.delete(currentSelectedModifier);
      } else if (this.canChangeModifierQuantity(group, modifier)) {
        currentSelectedModifier.quantity = modifier.quantity;
      }
    } else if (this.canAddModifierSelection(group)) {
      groupSet.add(modifier);
    }
  }

  private canChangeModifierQuantity(
    group: ItemModifierGroupUI,
    modifier: ISelectedModifier
  ): boolean {
    return (
      (group.maximum === 0 || modifier.quantity <= group.maximum) &&
      (modifier.maximumQty === 0 || modifier.quantity <= modifier.maximumQty)
    );
  }

  private canAddModifierSelection(group: ItemModifierGroupUI): boolean {
    return (
      group.maximum === 0 ||
      this.getTotalQuantityOfSelectedModifiers(group) < group.maximum
    );
  }

  isModifierQuantityAtMaximum(
    group: ItemModifierGroupUI,
    modifier: ISelectedModifier
  ): boolean {
    return (
      this.getTotalQuantityOfSelectedModifiers(group) > 0 &&
      (this.isModifierQuantityAtGroupMaximum(group) ||
        this.isModifierQuantityAtModifierMaximum(modifier))
    );
  }

  private isModifierQuantityAtGroupMaximum(
    group: ItemModifierGroupUI
  ): boolean {
    return (
      group.maximum > 0 &&
      this.getTotalQuantityOfSelectedModifiers(group) === group.maximum
    );
  }

  private isModifierQuantityAtModifierMaximum(
    modifier: ISelectedModifier
  ): boolean {
    return modifier.maximumQty > 0 && modifier.quantity === modifier.maximumQty;
  }

  private isModifierDisabled(
    modifier: ItemModifierUI,
    isSelectedItemDisabled: boolean,
    isMenuReadonly: boolean
  ): boolean {
    return (
      !isMenuReadonly &&
      (isSelectedItemDisabled ||
        modifier.isUnavailableDueToAlcohol ||
        !modifier.inStock)
    );
  }

  getTotalQuantityOfSelectedModifiers(group: ItemModifierGroupUI): number {
    return group.modifiers
      .filter((modifier) => modifier.isSelected)
      .reduce((a, modifier) => a + modifier.quantity, 0);
  }

  private readonly modifierCommentMap$ = new BehaviorSubject<
    Map<string, string>
  >(new Map<string, string>());

  restoreModifierStatesFromGroupList(modifierGroups: ItemModifierGroupUI[]) {
    const selectionMap = new Map<string, Set<ISelectedModifier>>();
    const commentMap = new Map<string, string>();
    this.restoreModifierStates(modifierGroups, selectionMap, commentMap);
    this.modifierSelectionMap$.next(selectionMap);
    this.modifierCommentMap$.next(commentMap);
  }

  private restoreModifierStates(
    modifierGroups: ItemModifierGroupUI[],
    selectionMap: Map<string, Set<ISelectedModifier>>,
    commentMap: Map<string, string>
  ) {
    modifierGroups.forEach((modifierGroup) => {
      modifierGroup.modifiers.forEach((modifier) => {
        const selectionGroupSet =
          selectionMap.get(modifierGroup.ownershipChainKey) ||
          new Set<ISelectedModifier>();

        selectionGroupSet.add({
          id: modifier.id,
          quantity: modifier.quantity,
          maximumQty: modifier.maximumQty
        });
        selectionMap.set(modifierGroup.ownershipChainKey, selectionGroupSet);

        if (modifier.commentText) {
          commentMap.set(modifier.ownershipChainKey, modifier.commentText);
        }

        this.restoreModifierStates(
          modifier.modifierGroups,
          selectionMap,
          commentMap
        );
      });
    });
  }

  private readonly _modifierGroupsForSelectedItem$: BehaviorSubject<
    ItemModifierGroupUI[]
  > = new BehaviorSubject<ItemModifierGroupUI[]>([]);

  readonly canMeetMinimumModGroupConstraints$: Observable<boolean> =
    combineLatest([
      this._modifierGroupsForSelectedItem$,
      this.locationStore.isAlcoholOrderingEnabled$
    ]).pipe(
      map(([modifierGroups, isAlcoholOrderingEnabled]) => {
        const modGroupThatCannotMeetMinimumConstraint = modifierGroups.find(
          (mg) => {
            if (mg.minimum === 0) return false;

            const possibleSelections = mg.modifiers.reduce((acc, mod) => {
              if (
                mod.inStock &&
                (!mod.alcohol.containsAlcohol ||
                  (mod.alcohol.containsAlcohol && isAlcoholOrderingEnabled))
              ) {
                acc += mg.allowMultiplesOfEachOption ? mod.maximumQty : 1;
              }
              return acc;
            }, 0);

            return possibleSelections < mg.minimum;
          }
        );

        return !modGroupThatCannotMeetMinimumConstraint;
      })
    );

  readonly isSelectedItemDisabled$: Observable<boolean> = combineLatest([
    this.selectedItem$,
    this.canMeetMinimumModGroupConstraints$
  ]).pipe(
    map(
      ([selectedItem, canMeetMinimumModGroupConstraints]) =>
        selectedItem.isDisabled || !canMeetMinimumModGroupConstraints
    )
  );

  readonly modifierGroupsForSelectedItem$: Observable<ItemModifierGroupUI[]> =
    combineLatest([
      this._modifierGroupsForSelectedItem$,
      this.modifierSelectionMap$,
      this.modifierCommentMap$,
      this.locationStore.isAlcoholOrderingEnabled$,
      this.isSelectedItemDisabled$,
      this.locationStore.isMenuReadonly$
    ]).pipe(
      map(
        ([
          modifierGroupsForSelectedItem,
          modifierSelectionMap,
          modifierCommentMap,
          isAlcoholOrderingEnabled,
          isSelectedItemDisabled,
          isMenuReadonly
        ]) =>
          this.mapModifierGroupsForSelectedItem(
            modifierGroupsForSelectedItem,
            modifierSelectionMap,
            modifierCommentMap,
            isAlcoholOrderingEnabled,
            isSelectedItemDisabled,
            isMenuReadonly
          )
      )
    );

  private mapModifierGroupsForSelectedItem(
    modifierGroups: ItemModifierGroupUI[],
    modifierSelectionMap: Map<string, Set<ISelectedModifier>>,
    modifierCommentMap: Map<string, string>,
    isAlcoholOrderingEnabled: boolean,
    isSelectedItemDisabled: boolean,
    isMenuReadonly: boolean
  ): ItemModifierGroupUI[] {
    for (let modifierGroup of modifierGroups) {
      for (let modifier of modifierGroup.modifiers) {
        modifier.modifierGroups = this.mapModifierGroupsForSelectedItem(
          modifier.modifierGroups,
          modifierSelectionMap,
          modifierCommentMap,
          isAlcoholOrderingEnabled,
          isSelectedItemDisabled,
          isMenuReadonly
        );

        const groupSet = modifierSelectionMap.get(
          modifierGroup.ownershipChainKey
        );

        modifier.isSelected =
          !!groupSet && !!this.findSelectedModifier(groupSet, modifier.id);

        modifier.commentText =
          modifierCommentMap.get(modifier.ownershipChainKey) || '';

        if (groupSet) {
          const currentSelectedModifier = this.findSelectedModifier(
            groupSet,
            modifier.id
          );
          modifier.quantity = currentSelectedModifier
            ? currentSelectedModifier.quantity
            : 0;

          // when a selection exists for a modifier group, all modifiers within that group should behave as if they are not default.
          modifier.isDefault = false;
        }

        modifier.isUnavailableDueToAlcohol =
          !isAlcoholOrderingEnabled && modifier.alcohol.containsAlcohol;

        modifier.disabled = this.isModifierDisabled(
          modifier,
          isSelectedItemDisabled,
          isMenuReadonly
        );
      }

      let isValid = true;
      const selectionCount = modifierGroup.modifiers.filter(
        (m) => m.isSelected
      ).length;

      const modifiersTotalQuantity =
        this.getTotalQuantityOfSelectedModifiers(modifierGroup);

      const totalSelected =
        selectionCount > modifiersTotalQuantity
          ? selectionCount
          : modifiersTotalQuantity;

      if (modifierGroup.minimum > 0 && totalSelected < modifierGroup.minimum) {
        isValid = false;
      } else if (
        modifierGroup.maximum > 0 &&
        totalSelected > modifierGroup.maximum
      ) {
        isValid = false;
      }

      modifierGroup.isSelectionValid = isValid;
    }

    return modifierGroups;
  }

  readonly areAllModifierGroupsValid$: Observable<boolean> =
    this.modifierGroupsForSelectedItem$.pipe(
      map((modifierGroups) =>
        this.flattenModifierGroupsForValidationDetermination(
          modifierGroups
        ).every((mg) => mg.isSelectionValid)
      )
    );

  readonly itemDisabledChipText$: Observable<string | null> = combineLatest([
    this.canMeetMinimumModGroupConstraints$,
    this.selectedItem$,
    this.i18nStore.menuItemsUnavailableBundle$
  ]).pipe(
    map(([canMeetMinModGroupConstraints, selectedItem, i18nBundle]) => {
      if (selectedItem.isUnavailableDueToAlcohol) {
        return i18nBundle.alcoholOrderingDisabled.toUpperCase();
      } else if (!canMeetMinModGroupConstraints) {
        return i18nBundle.currentlyUnavailable.toUpperCase();
      } else if (!selectedItem.inStock) {
        return i18nBundle.soldOut.toUpperCase();
      }

      return null;
    })
  );

  private flattenModifierGroupsForValidationDetermination(
    modifierGroups: ItemModifierGroupUI[]
  ): ItemModifierGroupUI[] {
    return modifierGroups
      .map((mg) => [
        mg,
        mg.modifiers
          .filter((m) => m.isSelected)
          .map((m) =>
            this.flattenModifierGroupsForValidationDetermination(
              m.modifierGroups
            )
          )
          .flat()
      ])
      .flat(2);
  }

  flattenModifierGroups(
    modifierGroups: ItemModifierGroupUI[]
  ): ItemModifierGroupUI[] {
    return modifierGroups
      .map((mg) => [
        mg,
        mg.modifiers
          .map((m) => this.flattenModifierGroups(m.modifierGroups))
          .flat()
      ])
      .flat(2);
  }

  set modifierGroupsForSelectedItem(v: ItemModifierGroupUI[]) {
    this._modifierGroupsForSelectedItem$.next(v);
  }

  clearSelectedItem() {
    this._selectedItemId$.next(null);
    this._selectedItemDetails$.next({
      quantity: 1,
      comment: ''
    });
    this._modifierGroupsForSelectedItem$.next([]);
    this.modifierSelectionMap$.next(new Map<string, Set<ISelectedModifier>>());
    this.modifierCommentMap$.next(new Map<string, string>());
  }

  readonly modifierGroupsWithDefaultModifiersForSelectedItem$: Observable<
    ItemModifierGroupUI[]
  > = combineLatest([
    this._modifierGroupsForSelectedItem$,
    this.isSelectedItemDisabled$,
    this.locationStore.isMenuReadonly$
  ]).pipe(
    map(([modifierGroups, isSelectedItemDisabled, isMenuReadonly]) =>
      this.flattenModifierGroups(modifierGroups).map((group) => ({
        ...group,
        modifiers: group.modifiers.filter((modifier) =>
          this.isDefaultModifierApplicable(
            modifier,
            isSelectedItemDisabled,
            isMenuReadonly
          )
        )
      }))
    )
  );

  private isDefaultModifierApplicable(
    modifier: ItemModifierUI,
    isSelectedItemDisabled: boolean,
    isMenuReadonly: boolean
  ): boolean {
    return (
      modifier.isDefault &&
      modifier.inStock &&
      !modifier.isUnavailableDueToAlcohol &&
      !this.isModifierDisabled(modifier, isSelectedItemDisabled, isMenuReadonly)
    );
  }

  private readonly defaultMaxItemDetailsCommentLength = 120;

  readonly maxUserCommentLength$: Observable<number> =
    this.locationStore.currentLocation$.pipe(
      map(
        (currentLocation) =>
          currentLocation?.systemType?.options?.maxItemCommentLength ??
          this.defaultMaxItemDetailsCommentLength
      )
    );

  isMultiModifierQuantitySelectionEnabled(group: ItemModifierGroupUI) {
    return group.allowMultiplesOfEachOption && group.maximum !== 1;
  }

  private findSelectedModifier(
    groupSet: Set<ISelectedModifier>,
    modifierId: string
  ): ISelectedModifier | undefined {
    return [...groupSet].find((modifier) => modifier.id === modifierId);
  }
}
